import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Heart, MessageCircle, Share2, Code2, Image as ImageIcon } from "lucide-react";
import { useState } from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

const mockThreads = [
  {
    id: 1,
    author: { name: "Alex Chen", avatar: "AC", role: "Engineer" },
    timestamp: "2 hours ago",
    title: "Tesla FSD Beta v12 First Impressions",
    content: "Just tested the new FSD beta on my Model 3. The improvements in urban navigation are incredible. Turns are so much smoother...",
    tags: ["Tesla", "FSD", "Engineering"],
    likes: 342,
    replies: 58,
    liked: false,
  },
  {
    id: 2,
    author: { name: "Mars Enthusiast", avatar: "ME", role: "Fan" },
    timestamp: "4 hours ago",
    title: "SpaceX Starship Timeline Thoughts",
    content: "Looking at the recent statements about Starship readiness. Do you think we'll see orbital refueling by Q3? The engineering is fascinating...",
    tags: ["SpaceX", "Starship", "Mars"],
    likes: 189,
    replies: 42,
    liked: false,
  },
  {
    id: 3,
    author: { name: "Neuralink Researcher", avatar: "NR", role: "Contributor" },
    timestamp: "6 hours ago",
    title: "Neural Interface Research Breakthroughs",
    content: "The recent Neuralink patient update shows promise in motor control restoration. This could revolutionize rehabilitation medicine.",
    tags: ["Neuralink", "Research", "Medical"],
    likes: 521,
    replies: 127,
    liked: false,
  },
];

export default function Threads() {
  const [threads, setThreads] = useState(mockThreads);
  const [composer, setComposer] = useState({ title: "", content: "" });
  const [expandComposer, setExpandComposer] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
    },
  };

  const toggleLike = (id: number) => {
    setThreads(threads.map(t => 
      t.id === id ? { ...t, liked: !t.liked, likes: t.liked ? t.likes - 1 : t.likes + 1 } : t
    ));
  };

  return (
    <div className="space-y-8">
      {/* Thread Composer */}
      <motion.div variants={itemVariants} className="border border-border p-6">
        <div className="space-y-4">
          <Input
            placeholder="What's on your mind? Ask a question, share insights..."
            value={composer.title}
            onChange={(e) => setComposer({ ...composer, title: e.target.value })}
            className="bg-transparent border-b border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-foreground transition-colors py-3 px-0"
          />
          
          {expandComposer && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-4"
            >
              <Textarea
                placeholder="Share your detailed thoughts, code snippets, citations..."
                value={composer.content}
                onChange={(e) => setComposer({ ...composer, content: e.target.value })}
                rows={4}
                className="bg-transparent border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-foreground transition-colors py-3 px-3 resize-none"
              />
              
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="border-border">
                  <Code2 className="h-4 w-4 mr-2" />
                  Code
                </Button>
                <Button size="sm" variant="outline" className="border-border">
                  <ImageIcon className="h-4 w-4 mr-2" />
                  Image
                </Button>
              </div>

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setExpandComposer(false)}>
                  Cancel
                </Button>
                <Button className="bg-foreground text-background hover:bg-foreground/90">
                  Post Thread
                </Button>
              </div>
            </motion.div>
          )}

          {!expandComposer && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setExpandComposer(true)}
              className="w-full p-3 border border-border hover:border-foreground transition-colors text-left text-muted-foreground text-sm"
            >
              Click to write a detailed thread...
            </motion.button>
          )}
        </div>
      </motion.div>

      {/* Threads Feed */}
      <motion.div
        className="space-y-4"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {threads.map((thread) => (
          <motion.div
            key={thread.id}
            variants={itemVariants}
            whileHover={{ y: -2 }}
            className="border border-border p-6 hover:border-foreground/50 transition-colors cursor-pointer group"
          >
            {/* Header */}
            <div className="flex items-start gap-4 mb-4">
              <Avatar className="h-10 w-10">
                <AvatarFallback className="text-xs font-medium bg-muted">
                  {thread.author.avatar}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-foreground">{thread.author.name}</h3>
                  <Badge variant="outline" className="text-xs">{thread.author.role}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">{thread.timestamp}</p>
              </div>
            </div>

            {/* Content */}
            <div className="mb-4">
              <h2 className="text-lg font-medium text-foreground mb-2 group-hover:opacity-70 transition-opacity">
                {thread.title}
              </h2>
              <p className="text-muted-foreground text-sm leading-relaxed line-clamp-3">
                {thread.content}
              </p>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-4">
              {thread.tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-border">
              <div className="flex items-center gap-6 text-xs text-muted-foreground">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  onClick={() => toggleLike(thread.id)}
                  className={`flex items-center gap-1 transition-colors ${
                    thread.liked ? "text-red-500" : "group-hover:text-foreground"
                  }`}
                >
                  <Heart className={`h-4 w-4 ${thread.liked ? "fill-current" : ""}`} />
                  {thread.likes}
                </motion.button>
                <button className="flex items-center gap-1 hover:text-foreground transition-colors">
                  <MessageCircle className="h-4 w-4" />
                  {thread.replies}
                </button>
                <button className="flex items-center gap-1 hover:text-foreground transition-colors">
                  <Share2 className="h-4 w-4" />
                  Share
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}
